import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import type { BulkReplacePromptDto } from "@/features/prompts/api/prompts.api";

interface BulkReplaceConfirmDialogProps {
  prompts: BulkReplacePromptDto[];
  open: boolean;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function BulkReplaceConfirmDialog({
  prompts,
  open,
  isPending,
  onOpenChange,
  onConfirm,
}: BulkReplaceConfirmDialogProps) {
  const { t } = useTranslation();
  const globalCount = prompts.filter((prompt) => prompt.isGlobal).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            {t("prompts.replace.confirmTitle", "Replace in {{count}} prompts?", {
              count: prompts.length,
            })}
          </DialogTitle>
          <DialogDescription>
            {t(
              "prompts.replace.confirmDescription",
              "Each selected prompt gets one new version. Unchecked prompts stay as they are. You can revert from version history.",
            )}
          </DialogDescription>
        </DialogHeader>
        <ul className="max-h-60 space-y-2 overflow-y-auto text-[13px]">
          {prompts.map((prompt) => (
            <li key={prompt.promptId} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate font-medium">{prompt.name}</span>
              {prompt.isGlobal ? (
                <span className="shrink-0 text-[11px] text-warning-subtle-foreground">
                  {t("prompts.replace.globalFlag", "Global fallback")}
                </span>
              ) : (
                <span className="shrink-0 truncate text-[11px] text-muted-foreground">
                  {prompt.tenantName ?? prompt.usedIn}
                </span>
              )}
            </li>
          ))}
        </ul>
        {globalCount > 0 ? (
          <p className="text-[12px] text-warning-subtle-foreground">
            {t(
              "prompts.replace.globalWarning",
              "Global prompts are the fallback for every tenant.",
            )}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button type="button" disabled={isPending || prompts.length === 0} onClick={onConfirm}>
            {isPending
              ? t("prompts.replace.applying", "Applying…")
              : t("prompts.replace.confirm", "Replace")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
